
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import area from '@turf/area';
import { Position } from '@turf/helpers';
import length from '@turf/length';
import * as L from 'leaflet';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { usePathStore } from '../stores/pathsStore';
import { SimplePath } from '../models/SimplePath';
import RenderColors from '../services/colors';
import { SimplePoint } from '../models/SimplePoint';
import { Point } from '../models/Point';

@Component
export default class LeafletMapPolylineEdit extends Vue {
  @Prop() map!: L.Map;

  public pathStore = usePathStore();
  //We recenter when we receive a new path
  lastPathId = null as string | null;
  line!: L.Polyline;
  polygon!: L.Polygon;
  workArea: L.Polygon | null = null;
  startMarker: L.Marker | null = null;
  endMarker: L.Marker | null = null;

  async mounted() {
    this.addMapClickListener();
    this.createNewPolygon([]);
  }

  destroyed() {
    this.destroyResources();
  }

  deactivated() {
    this.destroyResources();
  }

  private destroyResources() {
    this.line?.remove();
    this.polygon?.remove();
    this.workArea?.remove();
    this.startMarker?.remove();
    this.endMarker?.remove();
  }

  @Watch('pathStore.selectedPath')
  onSelectedPathChange(path: SimplePath): void {
    this.renderPath(path);
  }

  @Watch('pathStore.editable')
  onEditableChange(): void {
    if (this.line) {
      if (this.pathStore.editable) this.line.pm.enable({ removeVertexOn: 'click' });
      else {
        this.line.pm.disable();
      }
      this.updateLineMarkers();
    } else {
      this.createNewPolyline([]);
    }
  }

  @Watch('pathStore.editWorkArea')
  onEditWorkAreaChange(value: boolean): void {
    if (this.line) {
      if (!value) this.line.pm.enable({ removeVertexOn: 'click' });
      else {
        this.line.pm.disable();
      }
    }

    if (this.workArea) {
      if (value) this.workArea.pm.enable({ removeVertexOn: 'click' });
      else {
        this.workArea.pm.disable();
      }
      this.workArea.setStyle({ color: value ? RenderColors.WorkArea : 'transparent' });
    }
  }

  @Watch('pathStore.pathAssociatedField')
  onPathAssociatedFieldChange(): void {
    this.polygon.remove();
    this.createNewPolygon(this.pathStore.associatedField.BoundaryPoints);
  }

  @Watch('pathStore.clearMap')
  onClearMapChange(): void {
    this.line?.remove();
    this.polygon?.remove();
    this.workArea?.remove();
    this.startMarker?.remove();
    this.endMarker?.remove();
  }

  @Watch('pathStore.addPath')
  onAddPathChange(value: boolean): void {
    if (value) {
      if (this.line) {
        this.line.setLatLngs([]);
        this.line.addTo(this.map);
        this.pathStore.updateCurrentLine(L.GeoJSON.latLngsToCoords(this.line.getLatLngs()));
      }
    }
  }

  @Watch('pathStore.revertPath')
  onRevertPathChange(): void {
    this.line.remove();
    this.createNewPolyline(this.pathStore.selectedPath?.Path as SimplePoint[]);
    this.pathStore.setSelectedPathLength(this.findPathLengthInMeters(this.line));
    this.pathStore.updateCurrentLine(L.GeoJSON.latLngsToCoords(this.line.getLatLngs()));
  }

  @Watch('pathStore.revertWorkArea')
  onRevertWorkAreaChange(): void {
    if (!this.workArea) return;
    this.workArea.remove();
    this.createNewWorkAreaPolygon(
      this.pathStore.currentWorkArea.map((value) => ({ Lat: value[1], Long: value[0], Alt: 0 }))
    );
  }

  @Watch('pathStore.deleteWorkArea')
  onDeleteWorkAreaChange() {
    if (!this.workArea) return;
    this.workArea.remove();
    this.createNewWorkAreaPolygon([]);
  }

  createNewPolyline(path: SimplePoint[]) {
    const points = path.map((val) => {
      return { lat: val.Lat, lng: val.Long };
    });
    this.line = new L.Polyline(points, { color: RenderColors.PlannedPath, opacity: 0.8, weight: 3 });
    this.line.addTo(this.map);
    this.addPolylineEditListener();
    if (this.pathStore.editable) this.line.pm.enable({ removeVertexOn: 'click' });
    else {
      this.line.pm.disable();
    }
  }

  createNewPolygon(boundary: Point[]) {
    const points = boundary.map((val) => {
      return { lat: val.Item1, lng: val.Item2 };
    });
    this.polygon = new L.Polygon(points, {
      color: RenderColors.Field,
      fillColor: 'transparent',
      opacity: 0.8,
      weight: 3
    });
    this.polygon.addTo(this.map);
  }

  createNewWorkAreaPolygon(boundary: SimplePoint[]) {
    if (this.workArea) this.workArea.remove();
    const points = boundary.map((val) => {
      return { lat: val.Lat, lng: val.Long };
    });
    this.workArea = new L.Polygon(points, {
      color: 'transparent',
      fillColor: RenderColors.WorkArea,
      opacity: 0.8,
      weight: 3
    });
    this.workArea?.addTo(this.map);

    this.updateWorkArea();
    this.addPolylgonEditListener();
  }

  addMapClickListener() {
    this.map?.off('click');

    this.map?.on('click', (e: L.LeafletMouseEvent) => {
      this.mapClickHandler(e);
    });
  }

  mapClickHandler(e: L.LeafletMouseEvent) {
    if (!this.pathStore.editable) return;

    if (this.pathStore.editWorkArea) {
      this.updateWorkAreaPolygon(e);
    } else {
      this.updateLine(e);
    }
  }

  renderPath(path: SimplePath) {
    if (path.WorkAreas != undefined && path.WorkAreas.length > 0) {
      const array = path.WorkAreas[0].map((value: SimplePoint) => new L.LatLng(value.Lat, value.Long));
      let workArea = L.GeoJSON.latLngsToCoords(array) as Position[];
      if (workArea == undefined || workArea[0] == undefined || workArea[0].length == 0) workArea = [];
      this.pathStore.setCurrentWorkArea(workArea);
      this.createNewWorkAreaPolygon(path.WorkAreas[0]);
    } else {
      this.pathStore.setCurrentWorkArea([]);
      this.createNewWorkAreaPolygon([]);
    }

    if (this.line) this.line.remove();
    if (!path) return;
    this.createNewPolyline(path.Path);
    if (this.line.getLatLngs().length != 0  && path.PathID !== this.lastPathId) {
      this.map.fitBounds(this.line.getBounds(), { maxZoom: 19 });
      this.lastPathId = path.PathID;
    }

    this.updateLineMarkers();
    this.pathStore.setSelectedPathLength(this.findPathLengthInMeters(this.line));
    this.pathStore.updateCurrentLine(L.GeoJSON.latLngsToCoords(this.line.getLatLngs()));
  }

  updateLineMarkers() {
    if (this.startMarker) this.startMarker.remove();
    if (this.endMarker) this.endMarker.remove();
    if (this.pathStore.editable) return;

    const points = this.line.getLatLngs() as L.LatLng[];
    if (points.length < 2) return;
    const firstPoint = points[0];
    this.startMarker = new L.Marker(firstPoint, {
      icon: this.generateMarkerIcon('M12,8A4,4 0 0,0 8,12A4,4 0 0,0 12,16A4,4 0 0,0 16,12A4,4 0 0,0 12,8Z'),
      interactive: false,
      pmIgnore: true
    });
    this.startMarker?.addTo(this.map);

    const lastPoint = points.slice(-1)[0];
    this.endMarker = new L.Marker(lastPoint, {
      icon: this.generateMarkerIcon('M18,18H6V6H18V18Z'),
      interactive: false,
      pmIgnore: true
    });
    this.endMarker?.addTo(this.map);

    this.line.bringToFront();
  }

  generateMarkerIcon(path: string) {
    const iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const iconPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');

    iconSvg.setAttribute('fill', 'white');
    iconSvg.setAttribute('stroke', 'black');

    iconPath.setAttribute('d', path);

    iconSvg.appendChild(iconPath);

    const icon = new XMLSerializer().serializeToString(iconSvg);

    return new L.Icon({ iconUrl: `data:image/svg+xml,${icon}`, iconSize: [24, 24], iconAnchor: [12, 12] });
  }

  private addPolylineEditListener() {
    this.line?.off('pm:edit');
    if (this.pathStore.edit || this.pathStore.addPathMode) this.line.pm.enable({ removeVertexOn: 'click' });
    else {
      this.line.pm.disable();
    }
    this.line.on('pm:edit', () => {
      this.pathStore.setSelectedPathLength(this.findPathLengthInMeters(this.line));
      this.pathStore.updateCurrentLine(L.GeoJSON.latLngsToCoords(this.line.getLatLngs()));
    });
  }

  private addPolylgonEditListener() {
    this.workArea?.on('pm:edit', () => {
      this.updateWorkArea();
    });
  }

  updateLine(e: L.LeafletMouseEvent) {
    if (!this.line) {
      this.createNewPolyline([]);
    }

    if (this.pathStore.editable) {
      this.line.pm.enable({ removeVertexOn: 'click' });
    } else {
      this.line.pm.disable();
    }
    const newPath = this.line.getLatLngs() as L.LatLng[];
    if (newPath.length == 0) {
      this.createNewPolyline([]);
      this.line.addLatLng(e.latlng);
    } else {
      const startPointCloser = this.isStartPointCloser(newPath[0], newPath[newPath.length - 1], e.latlng);
      if (startPointCloser) {
        newPath.splice(0, 0, e.latlng);
      } else {
        newPath.push(e.latlng);
      }
      this.line.setLatLngs(newPath);
    }

    this.line.pm.disable();
    this.line.pm.enable();
    this.updateLineMarkers();
    this.pathStore.setSelectedPathLength(this.findPathLengthInMeters(this.line));
    this.pathStore.updateCurrentLine(L.GeoJSON.latLngsToCoords(this.line.getLatLngs()));
  }

  updateWorkAreaPolygon(e: L.LeafletMouseEvent) {
    if (!this.workArea) {
      this.createNewWorkAreaPolygon([]);
    }
    this.addPointToWorkArea(e);
  }

  addPointToWorkArea(e: L.LeafletMouseEvent) {
    if (!this.workArea) return;
    const newPath = this.workArea.getLatLngs();
    if (newPath == undefined || newPath.length == 0) {
      this.workArea.setLatLngs([e.latlng]);
    } else {
      this.workArea.addLatLng(e.latlng);
    }

    this.updateWorkArea();
  }

  updateWorkArea() {
    if (!this.workArea) return;
    let totalArea = 0;
    let currentWorkArea: L.LatLng[][] = [];

    if (this.workArea.getLatLngs() != undefined) {
      currentWorkArea = this.workArea.getLatLngs() as L.LatLng[][];
      totalArea = area(this.workArea.toGeoJSON());
    }

    if (this.pathStore.editable) {
      this.workArea.pm.enable({ removeVertexOn: 'click' });
    } else {
      this.workArea.pm.disable();
    }

    if (currentWorkArea == undefined || currentWorkArea[0] == undefined || currentWorkArea.length == 0)
      currentWorkArea = [];

    const coords: Position[] = currentWorkArea[0].map((o) => [o.lng, o.lat]);
    this.pathStore.setCurrentWorkArea(coords);
    this.pathStore.setSelectedWorkAreaArea(Math.round(totalArea));
  }

  /**
   * Checks if the start point or end point is closest to the clicked point.
   * Returns true if the start point is closest to the clicked point.
   */
  isStartPointCloser(startPoint: L.LatLng, endPoint: L.LatLng, clickedPoint: L.LatLng): boolean {
    const distanceToStart = Math.hypot(startPoint.lat - clickedPoint.lat, startPoint.lng - clickedPoint.lng);
    const distanceToEnd = Math.hypot(endPoint.lat - clickedPoint.lat, endPoint.lng - clickedPoint.lng);
    return distanceToStart < distanceToEnd;
  }

  /**
   * Returns the length of the path in meters
   */
  findPathLengthInMeters(path: L.Polyline) {
    const points = path.toGeoJSON();
    const distance = length(points, { units: 'meters' });
    return Math.round(distance);
  }
}
